import React from "react";

//Common
import PageHeader from "../../common/PageHeader/PageHeader";

//App
import { StateFormContainer } from "./StateFormContainer";
import { StateProps } from "../../../types/state";
import useStatehook from "./hooks/useStatehook";
import StateView from "./StateView";
import MoreActionsMenu from "../../common/MoreActionsMenu/MoreActionsMenu";
import { MoreActionButton } from "../../common/Buttons";
import {
    Snackbar,
    ConfirmationDialog,
    DataGrid,
    Drawer,
    Chip,

} from "../../common/VFX-UI";
import SearchContainer from "../../common/Forms/SearchContainer/SearchContainer";
import { StateDto } from "../../../app/model/api/types/dto";
import { SearchDefaultValue } from "../../../store/types";
import { SearchProps } from "../../../types";
import { SearchFieldsDefaultValue } from "../../../utility/constants/constants";

const States: React.FC<StateProps> = (props) => {
    const { data, activateMutation, deleteMutation } = props;

    const {
        state,
        formContext,
        rowEvents,
        actionColumnAnchorEl,
        toggleNewViewHandler,
        toggleViewHandler,
        toggleEditViewHandler,
        toggleDeactivateHandler,
        toggleActivateHandler,
        handleDeactivate,
        handleActivate,
        onShowSuccess,
        onAddSnackbarClose,
        handleActionClick,
        setActionColumnAnchorEl,
        handleActiveToggleChange,
    } = useStatehook({
        deleteMutation: deleteMutation,
        activateMutation: activateMutation,
    });

    const openMenu = Boolean(actionColumnAnchorEl);

    const columns = [
        {
            dataField: "id",
            text: "ID",
            hidden: true,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
        },

        {
            dataField: "description",
            text: "Description",
            sort: true,
        },
        {
            dataField: "abbreviation",
            text: "Abbreviation",
            sort: true,
        },
        {
            dataField: "capital",
            text: "Capital",
            sort: true,
        },
        {
            dataField: "regionName",
            text: "Region",
            sort: true,
        },
        {
            dataField: "isActive",
            text: "Status",
            sort: false,
            formatter: (cell: boolean) => {
                return <Chip label={cell ? "Active" : "Inactive"} />;
            },
        },
        {
            dataField: "action",
            text: "",
            style: { width: "25px" },
            isDummyField: true,
            formatter: () => {
                return (
                    <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
                );
            },
        },
    ];

    const searchProps: SearchProps<StateDto> = {
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: { ...SearchFieldsDefaultValue, name: true },
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: formContext?.refetchGrid,
    };

    return (
        <>
            <div>
                <PageHeader>States</PageHeader>

                <SearchContainer
                    searchProps={searchProps}
                    activeToggle={formContext?.activeToggle ?? true}
                    handleActiveToggleChange={handleActiveToggleChange}
                    toggleNewViewHandler={toggleNewViewHandler}
                />

            </div>

            <Drawer show={state.toggleNewForm}>
                {state.toggleNewForm && (
                    <StateFormContainer
                        onClose={toggleNewViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleEditForm}>
                {state.toggleEditForm && formContext?.data && (
                    <StateFormContainer
                        onClose={toggleEditViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleView}>
                {state.toggleView && formContext?.data && (
                    <StateView
                        onClose={toggleViewHandler}
                        editAction={() => {
                            toggleEditViewHandler();
                        }}
                        toggleDeactivateHandler={toggleDeactivateHandler}
                        toggleActivateHandler={toggleActivateHandler}
                    />
                )}
            </Drawer>

            <div>
                <DataGrid
                    columns={columns}
                    data={data}
                    keyField="id"
                    rowEvents={rowEvents}
                    totalSize={formContext?.paging?.totalSize}
                    remote={true}
                    pageSize={formContext?.pageSize}
                    page={formContext?.paging?.currentPage}
                    handlePageChange={async (newPage, pageSize ,sortField, sortOrder) => {
                        if (formContext?.setCurrentPage && newPage) {
                            await formContext?.setCurrentPage(newPage);
                        }
                        if (formContext?.setCurrentPageSize && pageSize) {
                            await formContext?.setCurrentPageSize(pageSize);
                        }
                        if (formContext?.setSortBy && sortField && sortOrder) {
                            await formContext?.setSortBy({
                                field: sortField,
                                direction: sortOrder === "asc" ? 1 : -1,
                            });
                        }
                        formContext?.refetchGrid();
                    }}
                />
                {actionColumnAnchorEl && (
                    <MoreActionsMenu
                        isOpen={openMenu}
                        target={actionColumnAnchorEl}
                        toggle={() => {
                            setActionColumnAnchorEl(null);
                        }}
                        onViewClick={() => {
                            toggleViewHandler();
                            setActionColumnAnchorEl(null);
                        }}
                    />
                )}

                <ConfirmationDialog
                    open={state.isDeactivateDialogOpen}
                    onClose={toggleDeactivateHandler}
                    onConfirm={handleDeactivate}
                    contentTitle={`Deactivate ${formContext?.data?.name}`}
                    contentText={"Are you sure you want to continue?"}
                    confirmText={"Yes, Continue"}
                    closeText={"No, Go Back"}
                ></ConfirmationDialog>

                <ConfirmationDialog
                    open={state.isActivateDialogOpen}
                    onClose={toggleActivateHandler}
                    onConfirm={handleActivate}
                    contentTitle={`Activate ${formContext?.data?.name}`}
                    contentText={"Are you sure you want to continue?"}
                    confirmText={"Yes, Continue"}
                    closeText={"No, Go Back"}
                />

                {state.showSuccessSnackbar && (
                    <Snackbar
                        open={state.showSuccessSnackbar}
                        message={state.successMessage}
                        handleClose={onAddSnackbarClose}
                        severity="success"
                    />
                )}
            </div>
        </>
    );
};

export default States;
